// Flatten door replies and assignment replies into one table leadership can scan
// and export: what the door did with each row × who it was assigned to.

export interface IntakeResultRow {
  name: string;
  domain: string;
  outcome: string;
  reason: string;
  written?: boolean;
  // The Account the door resolved, and who the row was meant for. An "Assign anyway"
  // re-sends the row with both, since a blocked row has no owner of its own to read.
  account_id?: string;
  targetOwner?: string;
  // The assignment step. Absent when the row stayed in the bank on purpose.
  assignStatus?: string;
  // The write's own reason code: `blocked` when the person gate stopped it.
  assignReason?: string;
  assignOwner?: string;
  assignMessage?: string;
  exclusion_reason?: string;
  exclusion_detail?: string;
  // Every person-gate label, from the door's reply or the write's, whichever stopped the row.
  reasons?: string[];
  // From the write: whether an admin or leader may force past `reasons`, and whether they did.
  forceable?: boolean;
  forced?: boolean;
}

export function resultPair(row: IntakeResultRow): string {
  if (row.reason) return row.outcome + '/' + row.reason;
  return row.outcome;
}

export function distinctOutcomePairs(rows: IntakeResultRow[]): string[] {
  const seen: string[] = [];
  for (const row of rows) {
    const pair = resultPair(row);
    if (seen.includes(pair)) continue;
    seen.push(pair);
  }
  return seen;
}

// The door's outcome in words. The raw outcome/reason still goes to the export.
export function bankResultLabel(row: IntakeResultRow): string {
  if (row.outcome === 'created') return 'Added to the bank';
  if (row.outcome === 'adopted') return 'Already in the book';
  if (row.outcome === 'attached') return 'Matched an existing account';
  if (row.outcome === 'excluded') {
    if (row.reason === 'malformed') return 'Skipped — missing name or domain';
    // The person gate's labels are in the next column; a code here would repeat them badly.
    if (row.reasons?.length) return 'Not added';
    return 'Not added' + (row.reason ? ' — ' + row.reason.replace(/_/g, ' ') : '');
  }
  return row.outcome;
}

// `omair.khan@cartesia.ai` → `Omair Khan`: the dashboard's `name_from_email`, Scout's `ownerName`.
export function nameFromEmail(email: string): string {
  const localPart = (email || '').split('@')[0] ?? '';
  const pieces = localPart.replace(/_/g, '.').split('.').filter(piece => piece !== '');
  const name = pieces.map(piece => piece.slice(0, 1).toUpperCase() + piece.slice(1)).join(' ');
  return name || email;
}

// The parts of an assign_account answer the line reads.
export interface AssignAnswerFacts {
  outcome: string;
  written: boolean;
  owner?: string | null;
  forced?: boolean | null;
  reasons?: string[] | null;
}

// An approver's upload write in words, as the Slack lines word it (GRO-224 item 1):
// "Would assign to Omair Khan, forced over: customer; has a paid Cartesia plan", with "Would"
// dropped once written. Never the raw email, and never the reason stamped on the record
// ("assigned on upload") — that is Salesforce's, not the table's. Null for any other outcome.
export function assignedLine(answer: AssignAnswerFacts, ownerEmail: string): string | null {
  const written = answer.written === true;
  if (!written && answer.outcome !== 'dry_run') return null;
  const verb = written ? 'Assigned' : 'Would assign';
  const owner = nameFromEmail(answer.owner || ownerEmail);
  const reasons = answer.forced ? answer.reasons || [] : [];
  const forcedOver = reasons.length ? `, forced over: ${reasons.join('; ')}` : '';
  return `${verb} to ${owner}${forcedOver}`;
}

export function isAssigned(row: IntakeResultRow): boolean {
  return row.assignStatus === 'written';
}

// A strategist's row that reached the approval queue. Not an assignment: nothing
// changes in Salesforce until an approver says yes.
export function isRequested(row: IntakeResultRow): boolean {
  return row.assignStatus === 'requested';
}

export const INTAKE_RESULT_CSV_COLUMNS = [
  { label: 'Name', accessor: (row: IntakeResultRow) => row.name },
  { label: 'Domain', accessor: (row: IntakeResultRow) => row.domain },
  { label: 'Outcome', accessor: (row: IntakeResultRow) => row.outcome },
  { label: 'Reason', accessor: (row: IntakeResultRow) => row.reason },
  {
    label: 'Written',
    accessor: (row: IntakeResultRow) => (
      row.written === true ? 'true' : row.written === false ? 'false' : ''
    ),
  },
  { label: 'Reasons', accessor: (row: IntakeResultRow) => (row.reasons || []).join('; ') },
  { label: 'Owner', accessor: (row: IntakeResultRow) => row.assignOwner || '' },
  {
    label: 'Assignment',
    accessor: (row: IntakeResultRow) => row.assignMessage || row.assignStatus || '',
  },
];
